import { BlogArticle } from '../types';

// 主動回憶 vs 重複閱讀 → CTA: /module/recite
const article: BlogArticle = {
  slug: 'active-recall-vs-rereading',
  date: '2026-08-28',
  readingMinutes: 4,
  tags: ['溫書技巧', '記憶法', '學習方法'],

  locales: {
    zhHant: {
      title: '主動回憶 vs 重複閱讀：你嘅溫書方法可能一直都錯',
      excerpt: '重讀筆記 10 次，不如自我測驗 1 次。認知科學研究話你知：點解「考自己」先係最強嘅溫書方法。',
      keywords: '主動回憶, active recall, 溫書方法, 自我測驗, testing effect',
      contentHtml: `
        <p>打開筆記，由第一頁睇到最後一頁，覺得「嗯，都識喎」—— 但一入考場就乜都唔記得。如果你有呢個經歷，你唔係一個人：呢個係<strong>重複閱讀（re-reading）嘅假象</strong>。</p>

        <h2>點解重讀冇用？</h2>
        <p>重讀令你對內容「眼熟」，而眼熟嘅感覺會呃你以為自己識。但考試要嘅唔係「認得」，係「<strong>諗得返出嚟</strong>」—— 兩樣嘢用嘅係完全唔同嘅記憶機制。</p>

        <h2>咩係主動回憶（Active Recall）？</h2>
        <p>好簡單：<strong>合上本書，逼自己將內容諗返出嚟</strong>。認知科學研究（testing effect）反覆證實：自我測驗嘅記憶效果，係重複閱讀嘅<strong>兩倍以上</strong>，而且保持得更耐。</p>

        <table>
          <thead>
            <tr><th></th><th>重複閱讀</th><th>主動回憶</th></tr>
          </thead>
          <tbody>
            <tr><td>感覺</td><td>輕鬆、似係識</td><td>辛苦、會發現唔識</td></tr>
            <tr><td>實際效果</td><td>弱（眼熟假象）</td><td>強（真正記住）</td></tr>
            <tr><td>暴露弱點</td><td>❌ 唔會</td><td>✅ 即刻現形</td></tr>
          </tbody>
        </table>

        <h2>5 個主動回憶嘅具體做法</h2>
        <ol>
          <li><strong>字卡</strong>：正面問題、背面答案，遮住答案考自己</li>
          <li><strong>默書/默寫</strong>：唔睇書默出關鍵概念</li>
          <li><strong>空白紙測試</strong>：攞張白紙，將成個課題嘅重點默寫出嚟</li>
          <li><strong>做題目</strong>：past paper 本身就係主動回憶</li>
          <li><strong>教人</strong>：將個概念講俾同學/屋企人聽，講唔出就係未明</li>
        </ol>

        <div class="tip-box">
          <strong>💡 貼士：</strong>主動回憶嘅「辛苦感」正正係佢有效嘅原因。諗唔出嗰陣嘅掙扎，係記憶鞏固嘅過程 —— 唔好一唔記得就即刻揭答案，先諗多 10 秒。
        </div>

        <h2>點樣融入日常溫書？</h2>
        <p>最簡單嘅做法：<strong>溫書時間嘅 80% 用嚟考自己，20% 用嚟搵答案</strong>。即係話，與其用 1 個鐘重讀，不如用 15 分鐘睇重點，然後 45 分鐘遮住書本自我測驗。</p>

        <p>想即刻試？用 AI 將你嘅筆記變成字卡，隨機抽問自己：</p>
        <p><a class="blog-cta-button" href="/module/recite">📚 免費生成字卡開始自測 →</a></p>

        <blockquote>溫書唔係將內容「放入腦」，係練習將內容「攞出嚟」。</blockquote>
      `,
    },

    zhHans: {
      title: '主动回忆 vs 重复阅读：你的复习方法可能一直都错',
      excerpt: '重读笔记 10 次，不如自我测验 1 次。认知科学研究告诉你：为什么「考自己」才是最强的复习方法。',
      keywords: '主动回忆, active recall, 复习方法, 自我测验, testing effect',
      contentHtml: `
        <p>打开笔记，从第一页看到最后一页，觉得「嗯，都会」—— 但一进考场就什么都不记得。如果你有这个经历，你不是一个人：这就是<strong>重复阅读（re-reading）的假象</strong>。</p>

        <h2>为什么重读没用？</h2>
        <p>重读让你对内容「眼熟」，而眼熟的感觉会骗你以为自己会了。但考试要的不是「认得」，是「<strong>想得出来</strong>」—— 两样东西用的是完全不同的记忆机制。</p>

        <h2>什么是主动回忆（Active Recall）？</h2>
        <p>很简单：<strong>合上书本，逼自己把内容想出来</strong>。认知科学研究（testing effect）反复证实：自我测验的记忆效果，是重复阅读的<strong>两倍以上</strong>，而且保持得更久。</p>

        <table>
          <thead>
            <tr><th></th><th>重复阅读</th><th>主动回忆</th></tr>
          </thead>
          <tbody>
            <tr><td>感觉</td><td>轻松、像是会了</td><td>辛苦、会发现不会</td></tr>
            <tr><td>实际效果</td><td>弱（眼熟假象）</td><td>强（真正记住）</td></tr>
            <tr><td>暴露弱点</td><td>❌ 不会</td><td>✅ 立刻现形</td></tr>
          </tbody>
        </table>

        <h2>5 个主动回忆的具体做法</h2>
        <ol>
          <li><strong>字卡</strong>：正面问题、背面答案，遮住答案考自己</li>
          <li><strong>默书/默写</strong>：不看书默出关键概念</li>
          <li><strong>空白纸测试</strong>：拿张白纸，把整个课题的重点默写出来</li>
          <li><strong>做题目</strong>：past paper 本身就是主动回忆</li>
          <li><strong>教人</strong>：把概念讲给同学/家人听，讲不出就是没懂</li>
        </ol>

        <div class="tip-box">
          <strong>💡 提示：</strong>主动回忆的「辛苦感」正是它有效的原因。想不出来时的挣扎，是记忆巩固的过程 —— 不要一不记得就立刻翻答案，先多想 10 秒。
        </div>

        <h2>怎么融入日常复习？</h2>
        <p>最简单的做法：<strong>复习时间的 80% 用来考自己，20% 用来找答案</strong>。也就是说，与其用 1 个小时重读，不如用 15 分钟看重点，然后 45 分钟遮住书本自我测验。</p>

        <p>想立刻试？用 AI 把你的笔记变成字卡，随机抽问自己：</p>
        <p><a class="blog-cta-button" href="/module/recite">📚 免费生成字卡开始自测 →</a></p>

        <blockquote>复习不是把内容「放进脑」，是练习把内容「取出来」。</blockquote>
      `,
    },

    en: {
      title: 'Active Recall vs Re-reading: Your Study Method Might Be Wrong',
      excerpt: 'Re-reading your notes 10 times is worth less than testing yourself once. Cognitive science explains why quizzing yourself is the strongest study method.',
      keywords: 'active recall, study methods, testing effect, self testing, memory',
      contentHtml: `
        <p>You read your notes cover to cover and think "yep, I know this" — then blank out in the exam. If that sounds familiar, you're not alone: that's the <strong>illusion of re-reading</strong>.</p>

        <h2>Why re-reading doesn't work</h2>
        <p>Re-reading makes content look familiar, and familiarity tricks you into feeling prepared. But exams don't need "recognise" — they need "<strong>retrieve</strong>". Those are completely different memory mechanisms.</p>

        <h2>What is active recall?</h2>
        <p>Simple: <strong>close the book and force yourself to retrieve the content</strong>. Decades of cognitive research (the testing effect) confirm that self-testing produces <strong>more than double</strong> the retention of re-reading — and it lasts longer.</p>

        <table>
          <thead>
            <tr><th></th><th>Re-reading</th><th>Active recall</th></tr>
          </thead>
          <tbody>
            <tr><td>Feels like</td><td>Easy, "I know this"</td><td>Hard, exposes gaps</td></tr>
            <tr><td>Actual effect</td><td>Weak (familiarity illusion)</td><td>Strong (real retention)</td></tr>
            <tr><td>Reveals weak spots</td><td>❌ No</td><td>✅ Instantly</td></tr>
          </tbody>
        </table>

        <h2>5 concrete ways to use active recall</h2>
        <ol>
          <li><strong>Flashcards</strong>: question on the front, answer on the back — cover and test</li>
          <li><strong>Dictation/recall writing</strong>: write out key concepts from memory</li>
          <li><strong>The blank-page test</strong>: take a blank sheet and dump everything you know about a topic</li>
          <li><strong>Practice questions</strong>: past papers are active recall by definition</li>
          <li><strong>Teach someone</strong>: explain the concept to a friend — if you can't say it, you don't know it</li>
        </ol>

        <div class="tip-box">
          <strong>💡 Tip:</strong> The "struggle" of active recall is exactly why it works. That effort of almost-remembering is memory being built — don't flip to the answer instantly; wrestle for 10 more seconds.
        </div>

        <h2>How to build it into your routine</h2>
        <p>The simplest rule: <strong>spend 80% of study time testing yourself, 20% looking things up</strong>. Instead of re-reading for an hour, review key points for 15 minutes, then self-test for 45.</p>

        <p>Want to try it now? Turn your notes into flashcards with AI and quiz yourself in random order:</p>
        <p><a class="blog-cta-button" href="/module/recite">📚 Generate free flashcards and start self-testing →</a></p>

        <blockquote>Studying isn't putting content into your brain — it's practising taking it out.</blockquote>
      `,
    },
  },
};

export default article;
